import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import StudentAvatar from "../../components/StudentAvatar";
import { api, formatApiError } from "../../lib/api";
import { Plus, Pencil, Trash2, BedDouble, Tag } from "lucide-react";
import { toast } from "sonner";

export default function AdminRooms() {
  const [cats, setCats] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", monthly_rent: "", security_deposit: "", description: "" });
  const [catEditing, setCatEditing] = useState(null);
  const [roomOpen, setRoomOpen] = useState(false);
  const [roomForm, setRoomForm] = useState({ number: "", category_id: "", capacity: 2, floor: "", notes: "" });
  const [roomEditing, setRoomEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const [c, r] = await Promise.all([api.get("/admin/room-categories"), api.get("/admin/rooms")]);
    setCats(c.data); setRooms(r.data);
  }
  useEffect(() => { load(); }, []);

  // --- categories
  function openCatCreate() { setCatEditing(null); setCatForm({ name: "", monthly_rent: "", security_deposit: "", description: "" }); setCatOpen(true); }
  function openCatEdit(c) { setCatEditing(c); setCatForm({ name: c.name, monthly_rent: c.monthly_rent, security_deposit: c.security_deposit, description: c.description || "" }); setCatOpen(true); }
  async function saveCat(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...catForm, monthly_rent: Number(catForm.monthly_rent), security_deposit: Number(catForm.security_deposit) };
      if (catEditing) await api.put(`/admin/room-categories/${catEditing.id}`, payload);
      else await api.post("/admin/room-categories", payload);
      toast.success(catEditing ? "Category updated" : "Category added");
      setCatOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  async function removeCat(c) {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/admin/room-categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  // --- rooms
  function openRoomCreate() {
    setRoomEditing(null);
    setRoomForm({ number: "", category_id: cats?.[0]?.id || "", capacity: 2, floor: "", notes: "" });
    setRoomOpen(true);
  }
  function openRoomEdit(r) {
    setRoomEditing(r);
    setRoomForm({ number: r.number, category_id: r.category_id, capacity: r.capacity, floor: r.floor || "", notes: r.notes || "" });
    setRoomOpen(true);
  }
  async function saveRoom(e) {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...roomForm, capacity: Number(roomForm.capacity) };
      if (roomEditing) await api.put(`/admin/rooms/${roomEditing.id}`, payload);
      else await api.post("/admin/rooms", payload);
      toast.success(roomEditing ? "Room updated" : "Room added");
      setRoomOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  async function removeRoom(r) {
    if (!window.confirm(`Delete room ${r.number}?`)) return;
    try { await api.delete(`/admin/rooms/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const totalBeds = (rooms || []).reduce((a, b) => a + (b.capacity || 0), 0);
  const occupied = (rooms || []).reduce((a, b) => a + (b.occupied_beds || 0), 0);
  const free = totalBeds - occupied;

  return (
    <AppShell>
      <PageHeader
        overline="Accommodation"
        title="Rooms & beds"
        subtitle="Set up room categories, individual rooms, and see live bed availability."
      />

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Stat label="Total beds" value={totalBeds} tint="bg-[#F4F1EA] text-[#75716E]" />
        <Stat label="Occupied" value={occupied} tint="bg-[#FBEED7] text-[#8C6D31]" />
        <Stat label="Available" value={free} tint="bg-[#DCE3D6] text-[#4A5D3E]" />
      </div>

      {/* Categories */}
      <Panel
        title={<span className="inline-flex items-center gap-2"><Tag className="h-4 w-4 text-[#A67B5B]" />Room categories</span>}
        action={<button onClick={openCatCreate} data-testid="add-category-btn" className="bg-terracotta text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add category</button>}
        className="mb-6"
      >
        {cats === null ? <div className="text-sm text-warm">Loading…</div>
          : cats.length === 0 ? <EmptyState title="No categories yet" hint="Create at least one room category (e.g. AC double, non-AC triple)." />
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cats.map((c) => (
                  <div key={c.id} className="border border-warm rounded-lg p-4 card-lift" data-testid={`category-${c.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-deep">{c.name}</div>
                        {c.description && <div className="text-xs text-warm mt-1 leading-relaxed">{c.description}</div>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openCatEdit(c)} className="p-1.5 hover:bg-[#F4F1EA] rounded-md" data-testid={`edit-category-${c.id}`}><Pencil className="h-3.5 w-3.5 text-warm" /></button>
                        <button onClick={() => removeCat(c)} className="p-1.5 hover:bg-[#F5D8D8] rounded-md"><Trash2 className="h-3.5 w-3.5 text-[#8C3F3F]" /></button>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-[#FDFBF7] border border-warm rounded-md px-3 py-2">
                        <div className="uppercase tracking-wider text-warm text-[10px]">Monthly rent</div>
                        <div className="font-body font-semibold text-deep tracking-tight">₹ {Number(c.monthly_rent).toLocaleString()}</div>
                      </div>
                      <div className="bg-[#FDFBF7] border border-warm rounded-md px-3 py-2">
                        <div className="uppercase tracking-wider text-warm text-[10px]">Deposit</div>
                        <div className="font-body font-semibold text-deep tracking-tight">₹ {Number(c.security_deposit).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </Panel>

      {/* Rooms */}
      <Panel
        title={<span className="inline-flex items-center gap-2"><BedDouble className="h-4 w-4 text-[#A67B5B]" />Rooms</span>}
        action={<button onClick={openRoomCreate} disabled={!cats?.length} data-testid="add-room-btn" className="bg-terracotta text-white px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50"><Plus className="h-4 w-4" /> Add room</button>}
      >
        {rooms === null ? <div className="text-sm text-warm">Loading…</div>
          : rooms.length === 0 ? <EmptyState title="No rooms yet" hint={!cats?.length ? "Create a category first, then add rooms." : "Add a room to get started."} />
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {rooms.map((r) => (
                  <article key={r.id} className="border border-warm rounded-lg p-4 card-lift bg-white" data-testid={`room-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-heading text-2xl text-deep leading-none">Room {r.number}</div>
                        <div className="text-xs uppercase tracking-wider text-[#A67B5B] mt-1">{r.category?.name || "—"}{r.floor ? ` · ${r.floor}` : ""}</div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openRoomEdit(r)} className="p-1.5 hover:bg-[#F4F1EA] rounded-md"><Pencil className="h-3.5 w-3.5 text-warm" /></button>
                        <button onClick={() => removeRoom(r)} className="p-1.5 hover:bg-[#F5D8D8] rounded-md"><Trash2 className="h-3.5 w-3.5 text-[#8C3F3F]" /></button>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2 text-xs">
                      <span className="px-2.5 py-0.5 rounded-full bg-[#DCE3D6] text-[#4A5D3E] font-medium">{r.free_beds} free</span>
                      <span className="px-2.5 py-0.5 rounded-full bg-[#FBEED7] text-[#8C6D31] font-medium">{r.occupied_beds}/{r.capacity} occupied</span>
                    </div>

                    {/* Bed grid */}
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {Array.from({ length: r.capacity }).map((_, i) => {
                        const bedNo = i + 1;
                        const occupant = r.occupants.find((o) => o.bed_number === bedNo);
                        return (
                          <div key={bedNo} className={`text-xs rounded-md p-2 border ${occupant ? "border-warm bg-[#FDFBF7]" : "border-dashed border-[#DCE3D6] bg-white"}`} title={occupant?.name || "Free"}>
                            <div className="text-[10px] uppercase tracking-wider text-warm">Bed {bedNo}</div>
                            {occupant ? (
                              <div className="mt-1 flex items-center gap-1.5">
                                <StudentAvatar student={occupant} size={20} />
                                <span className="truncate text-deep">{occupant.name?.split(" ")[0]}</span>
                              </div>
                            ) : (
                              <div className="mt-1 text-[#4A5D3E] font-medium">Available</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
            )}
      </Panel>

      {/* Category Modal */}
      {catOpen && (
        <Modal title={catEditing ? "Edit category" : "New room category"} onClose={() => setCatOpen(false)}>
          <form onSubmit={saveCat} className="grid sm:grid-cols-2 gap-4">
            <FieldL label="Name *"><input required value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className={input} placeholder="AC Double Sharing" data-testid="category-name" /></FieldL>
            <FieldL label=""><span className="invisible">.</span></FieldL>
            <FieldL label="Monthly rent (₹) *"><input required type="number" min="0" value={catForm.monthly_rent} onChange={(e) => setCatForm({ ...catForm, monthly_rent: e.target.value })} className={input} data-testid="category-rent" /></FieldL>
            <FieldL label="Security deposit (₹) *"><input required type="number" min="0" value={catForm.security_deposit} onChange={(e) => setCatForm({ ...catForm, security_deposit: e.target.value })} className={input} data-testid="category-deposit" /></FieldL>
            <div className="sm:col-span-2"><FieldL label="Description"><textarea rows={2} value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} className={input} /></FieldL></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setCatOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-category-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Room Modal */}
      {roomOpen && (
        <Modal title={roomEditing ? "Edit room" : "New room"} onClose={() => setRoomOpen(false)}>
          <form onSubmit={saveRoom} className="grid sm:grid-cols-2 gap-4">
            <FieldL label="Room number *"><input required value={roomForm.number} onChange={(e) => setRoomForm({ ...roomForm, number: e.target.value })} className={input} placeholder="101 / A-2" data-testid="room-number" /></FieldL>
            <FieldL label="Category *">
              <select required value={roomForm.category_id} onChange={(e) => setRoomForm({ ...roomForm, category_id: e.target.value })} className={input} data-testid="room-category">
                <option value="">Select…</option>
                {cats?.map((c) => <option key={c.id} value={c.id}>{c.name} — ₹{Number(c.monthly_rent).toLocaleString()}/mo</option>)}
              </select>
            </FieldL>
            <FieldL label="Capacity (beds) *"><input required type="number" min="1" max="10" value={roomForm.capacity} onChange={(e) => setRoomForm({ ...roomForm, capacity: e.target.value })} className={input} data-testid="room-capacity" /></FieldL>
            <FieldL label="Floor"><input value={roomForm.floor} onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })} className={input} placeholder="Ground / 1st" /></FieldL>
            <div className="sm:col-span-2"><FieldL label="Notes"><textarea rows={2} value={roomForm.notes} onChange={(e) => setRoomForm({ ...roomForm, notes: e.target.value })} className={input} /></FieldL></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setRoomOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-room-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div className="bg-white border border-warm rounded-lg p-5">
      <div className={`inline-block h-1.5 w-12 rounded-full mb-3 ${tint}`} />
      <div className="font-body font-semibold text-3xl text-deep tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-warm mt-1">{label}</div>
    </div>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
function FieldL({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label || "\u00A0"}</span>{children}</label>; }
